import { useState } from 'react'
import { Typography, useTheme, MenuItem, Stack, Button } from '@mui/material'
import BoxWithBorder from 'components/atoms/container/BoxWithBorder'
import CharacterAvatar from './CharacterAvatar'
import colors from 'styles/colors'
import { useTranslation } from 'react-i18next'
import { ROLE_PLAY_TYPES } from 'services/constants'
import { StyledSelect } from 'components/atoms/input/StyledInputs'
import { convertSecsToMins } from 'utils/formatDateTime'
import { botName } from 'utils/formatText'
import InCardStack from 'components/atoms/container/InCardStack'
import { checkIsMobile, isiPad } from 'utils/deviceCheck'
import RolePlayCallVoiceVisualizer from './RolePlayCallVoiceVisualizer'

const PublicRolePlayInteractionCard = ({
  rolePlay,
  buttonText,
  onButtonClick,
  practiceType,
  setPracticeType,
  children,
  showAudioIcon,
  type,
  completeTime,
  callTimer,
  callClient
}) => {
  const theme = useTheme()
  const { t } = useTranslation()
  const [isBotSpeaking, setIsBotSpeaking] = useState(true)
  const [isBotReady, setIsBotReady] = useState(false)

  return (
    <BoxWithBorder
      sx={{
        border: 1,
        borderColor: theme.palette.primary.main,
        background: theme.palette.primary.main,
        borderWidth: 1,
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        color: theme.palette.background.default,
        ...theme.publicRolePlay.scenario.rolePlayCard,
        ...(type === ROLE_PLAY_TYPES.LIVE_CALL &&
          theme.publicRolePlay?.callFooter)
      }}>
      <Stack sx={theme.publicRolePlay.scenario.interactionStack}>
        <CharacterAvatar
          rolePlay={rolePlay}
          name={botName(rolePlay)}
          sx={{
            ...theme.publicRolePlay.scenario.avatarBorder,
            ...theme.publicRolePlay.scenario.avatarSize
          }}
          iconBoxSx={theme.publicRolePlay.scenario.audioIcon}
          showAudioIcon={showAudioIcon && isBotReady}
          isBotSpeaking={isBotSpeaking}
        />
        <RolePlayCallVoiceVisualizer
          client={callClient}
          isBotReady={isBotReady}
          setIsBotReady={setIsBotReady}
          isBotSpeaking={isBotSpeaking}
          setIsBotSpeaking={setIsBotSpeaking}
        />
        <InCardStack spacing={0}>
          <Typography
            variant='h1'
            color={colors.White}
            sx={theme.publicRolePlay.scenario.botName}>
            {botName(rolePlay).split(' ')[0]}
          </Typography>

          {callTimer && (
            <Typography variant='body1' color={colors.White}>
              {callTimer}
            </Typography>
          )}
        </InCardStack>

        <Stack sx={theme.publicRolePlay.scenario.actionStack}>
          {setPracticeType && (
            <StyledSelect
              value={practiceType}
              onChange={e => setPracticeType(e.target.value)}
              sx={theme.publicRolePlay.scenario.interactionSelect}
              displayEmpty>
              <MenuItem value='' disabled>
                {t('rolePlay.interactionType')}
              </MenuItem>
              <MenuItem value={ROLE_PLAY_TYPES.TEXT_CHAT}>
                {t('rolePlay.textChat')}
              </MenuItem>

              {!(checkIsMobile() || isiPad()) ? (
                <MenuItem value={ROLE_PLAY_TYPES.LIVE_CALL}>
                  {t('rolePlay.liveCall')}
                </MenuItem>
              ) : (
                <MenuItem disabled>
                  <Typography variant='body1' sx={{ fontSize: 13 }}>
                    <b>
                      {t('publicRolePlay.voiceCallAvailableOnDesktop.part1')}
                    </b>
                    {t('publicRolePlay.voiceCallAvailableOnDesktop.part2')}
                  </Typography>
                </MenuItem>
              )}
            </StyledSelect>
          )}

          {completeTime && (
            <Typography variant='h3' color={colors.White}>
              {`${t(type === ROLE_PLAY_TYPES.LIVE_CALL ? 'rolePlay.call' : 'rolePlay.textChat')} ${t('common.completed')} `}
              {type === ROLE_PLAY_TYPES.LIVE_CALL &&
                `(${convertSecsToMins(completeTime).trim()})`}
            </Typography>
          )}

          {buttonText && (
            <Button
              onClick={onButtonClick}
              disabled={practiceType === ''}
              sx={theme.publicRolePlay.scenario.actionButton}>
              {buttonText}
            </Button>
          )}
          {children}
        </Stack>
      </Stack>
    </BoxWithBorder>
  )
}

export default PublicRolePlayInteractionCard
